import { EquipmentType, NozzleDirection, NozzleType } from "@/types/common"
import { LineType, PortTypes } from "../constant"
import { Equipment } from "@/services/project/equipment/equipment.model"
import { Cell, PointLike } from "@antv/x6"
import { FlowTemplate } from "@/store/flowsheet/flowsheet.slice"

export enum CellType {
  Line,
  Node,
}

export enum LineJumpType {
  Circle = 'circle',
  Blank = 'blank',
  Wave = 'wave'
}

export enum NodeType {
  SvgNode = 'svgNode',
}

export enum NodeShapes {
  UnitTag = 'UnitTagNode',
  SvgNode = 'svgNode',
  TextNode = 'TextNode',
  ImageNode = 'ImageNode',
  Edge = 'edge',
  SvgStream = 'SvgStream',
  LineJump = 'LineJump'
}

export interface CellRecord {
  id: CellId
  zIndex: number
}

export interface NodeEquipmentInfoRecord {
  id: number
  name: string
  type: EquipmentType
  nozzleTemps?: Equipment.CustomNozzle[]
}

export interface SvgNozzleLayoutRecord {
  width?: number
  height?: number
  x: number
  y: number
  angle?: number
}

export interface NozzleRecord {
  id: CellId
  name: string
  direction?: NozzleDirection
  shapeId?: number
  image?: string
  type: NozzleType
  group: PortTypes
  layout?: SvgNozzleLayoutRecord
  nozzleEdgeName?: string
  nozzleEdge?: boolean
}

export interface BasicNozzle {
  id: CellId
  group: PortTypes
  layout?: SvgNozzleLayoutRecord
}

export interface NodeState {
  id: CellId
  nodeType: NodeType
  tag: string
  angle?: number
  position?: {
    x: number
    y: number
  },
  size: {
    width: number
    height: number
  },
  nozzles: NozzleRecord[]
  image: string
  equipment: NodeEquipmentInfoRecord
  showTag?: boolean
  tagOption?: UnitTagState
  nozzleEdges?: Equipment.NozzleEdge[]
  customNozzles?: NozzleRecord[]
  lineNozzles?: BasicNozzle[]
  simArgs?: any,
  flip?: Flip
}

export enum Flip {
  None,
  X,
  Y
}

export interface StreamConnection extends Cell.Properties {
  id: string
  data: SvgStreamDataState
}

export interface CustomNozzleState {
  id: string
  x: number
  y: number
  direction: NozzleDirection
  name: string
  group: PortTypes
}

export interface SvgStreamDataState {
  streamId: string, // the source stream ID
  from: string
  to: string
  streamTag: string
  refStreamId?: string // the reference line ID
  flip?: Flip
}

export interface NodeRecord extends NodeState {
  position: {
    x: number
    y: number
  }
}


export interface LineEndToPort {
  cell: CellId
  port: CellId
}

export interface LineEndToEdge {
  cell: CellId
  anchor: {
    args: {length: number},
    name: string
  }
}

export interface ILineEnd {
  x?: number
  y?: number
  cell?: CellId
  port?: CellId
  anchor?: {
    args: {length: number},
    name: string
  }
}

export type LineEnd = PositionLike | LineEndToPort | LineEndToEdge

export enum LineEndEnum{
  Point,
  LinkToPort,
  LinkToEdge,
}

export interface LineRecord extends CellRecord {
  type: LineType
  tag: string
  zIndex: number
  source: LineEnd
  target: LineEnd
  vertices: PositionLike[]
  showTag?: boolean
  tagOption?: UnitTagState
  streamId?: string
  hide?: boolean
}

export interface ConnectPoint extends PositionLike {
  lineId: CellId
}

export interface SheetContent {
  nodes: NodeRecord[],
  lines: LineRecord[],
  polylines?: Cell.Properties[],
  connections: StreamConnection[],
  jumps?: LineJumpRecord[],
  overridedNodes?: Cell.Properties[],
  name: string,
  id: string,
  image?: string
}

export interface TagState {
  [key: string]: number
}

export interface SetOperation {
  id: string
  tag: string
  sourceUnit: string
  sourceVar: string
  targetUnit: string
  targetVar: string
  isNew?: boolean
}

export interface Spreadsheet {
  id: string
  tag: string
  data: any
}
export interface FlowsheetContent {
  nodeImages: {[key: string]: string},
  nozzleImages: { [key: string]: string },
  sheets: SheetContent[],
  tagState: TagState
  setOperations: SetOperation[]
  spreadsheets: Spreadsheet[],
  tempCells?: Cell.Properties[]
  template?: FlowTemplate
  version?: number
}

export interface UnitTagState extends TextNodeState {
  unit: string
  offset: {
    x: number
    y: number
    cx?: number
    cy?: number
  }
}

export interface TextNodeState {
  text?: string
  size: {
    width: number
    height: number
  }
  style?: {
    fontSize: number
    fontWeight: number
    color: string
  }
  angle?: number
}

export interface ImageNodeState {
  url: string
  size: {
    width: number
    height: number
  }
  angle?: number
}

interface PortItem {
  id: string,
  position: PointLike
}

export interface LineJumpData {
  type: LineJumpType
  streamId: string
  image: string
}
export interface LineJumpState {
  id: string
  size: {
    width: number
    height: number
  },
  angle: number,
  position: {
    x: number
    y: number
  }
  ports: PortItem[]
  data: LineJumpData
  zIndex?: number
}

export interface LineJumpRecord extends LineJumpState {

}

export interface CellDataWithId extends Cell.Properties {
  id: string
}